import React from "react";
import { Button, Input } from "antd";
import { SearchOutlined } from "@ant-design/icons";

const { Search } = Input;

interface IFilterSelectedTagsSection {
    selectedCount: number;
    totalCount: number;
    selectedTags: React.ReactNode;
}

interface IFilterSearchSection {
    searchPlaceholder: string;
    searchValue: string;
    onSearchChange: (value: string) => void;
    onSearchReset: () => void;
    headerExtraContent?: React.ReactNode;
    searchInputType?: "input" | "search";
    searchContainerStyle?: React.CSSProperties;
}

interface IFilterInternals extends IFilterSearchSection,IFilterSelectedTagsSection {
    mainContent: React.ReactNode;
    contentPadding?: string;
    containerPadding?: string;
    mainContentMaxHeight?: string;
    useSimpleLayout?: boolean;
}

interface IFilterMainContent {
    mainContent: React.ReactNode;
    containerClassName?: string;
    maxHeight?: string;
}

export const FilterSelectedTagsSection: React.FC<
    IFilterSelectedTagsSection
> = ({ selectedCount, totalCount, selectedTags }) => (
    <div className="filter-dropdown-head py-3 px-4 flex-shrink-0">
        <div className="filter-dropdown-selection-labels pb-3">
            <span className="selected">{selectedCount} Selected</span>
            <span className="total">Total {totalCount}</span>
        </div>
        <div className="overflow-auto" style={{ maxHeight: "120px" }}>
            {selectedTags}
        </div>
    </div>
);

export const FilterSearchSection: React.FC<IFilterSearchSection> = ({
    searchPlaceholder,
    searchValue,
    onSearchChange,
    onSearchReset,
    headerExtraContent,
    searchInputType = "input",
    searchContainerStyle,
}) => {
    const renderSearchInput = () => {
        if (searchInputType === "search") {
            return (
                <div
                    className="d-flex align-items-center gap-3 mb-3"
                    style={{ height: 40, ...searchContainerStyle }}
                >
                    <Search
                        placeholder={searchPlaceholder}
                        value={searchValue}
                        onChange={(e) => onSearchChange(e.target.value)}
                        allowClear
                        style={{ width: 400 }}
                    />
                    <Button
                        type="link"
                        danger
                        onClick={onSearchReset}
                        className="advanced-filters-reset"
                    >
                        Reset
                    </Button>
                </div>
            );
        }

        return (
            <div
                className={`d-flex align-items-center gap-3 ${
                    headerExtraContent ? "mt-3" : ""
                }`}
                style={{ height: 40 }}
            >
                <Input
                    placeholder={searchPlaceholder}
                    value={searchValue}
                    onChange={(e) => onSearchChange(e.target.value)}
                    size="middle"
                    className="filter-search-input"
                    suffix={
                        <SearchOutlined
                            style={{
                                color: "#B0B7C3",
                                fontSize: 18,
                            }}
                        />
                    }
                />
                <Button
                    type="link"
                    danger
                    onClick={onSearchReset}
                    className="filter-reset-button"
                >
                    Reset
                </Button>
            </div>
        );
    };

    return (
        <div className="filter-search-container flex-shrink-0">
            {headerExtraContent}
            {renderSearchInput()}
        </div>
    );
};

export const FilterMainContent: React.FC<IFilterMainContent> = ({
    mainContent,
    containerClassName = "",
    maxHeight = "250px",
}) => {
    const style: React.CSSProperties = { minHeight: 0 };
    if (maxHeight !== "none") {
        style.maxHeight = maxHeight;
    }

    return (
        <div className={`flex-grow-1 ${containerClassName}`} style={style}>
            {mainContent}
        </div>
    );
};

export const FilterInternals: React.FC<IFilterInternals> = ({
    selectedCount,
    totalCount,
    selectedTags,
    searchPlaceholder,
    searchValue,
    onSearchChange,
    onSearchReset,
    headerExtraContent,
    searchInputType = "input",
    searchContainerStyle,
    mainContent,
    contentPadding = "px-3 py-4",
    containerPadding,
    mainContentMaxHeight = "250px",
    useSimpleLayout = false,
}) => {
    if (useSimpleLayout) {
        // Simple layout for RevstackFiltersMenu - fixed search, scrollable content
        const content = (
            <>
                <FilterSearchSection
                    searchPlaceholder={searchPlaceholder}
                    searchValue={searchValue}
                    onSearchChange={onSearchChange}
                    onSearchReset={onSearchReset}
                    headerExtraContent={headerExtraContent}
                    searchInputType={searchInputType}
                    searchContainerStyle={searchContainerStyle}
                />
                <FilterMainContent
                    mainContent={mainContent}
                    maxHeight={mainContentMaxHeight}
                    containerClassName="flex-grow-1"
                />
            </>
        );

        return (
            <div
                className={containerPadding || undefined}
                style={{
                    height: "400px",
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                {content}
            </div>
        );
    }

    // Complex layout for TopLevelFilters
    const searchAndMainContent = (
        <div
            className={`${contentPadding} flex-grow-1 overflow-hidden d-flex flex-column`}
        >
            <FilterSearchSection
                searchPlaceholder={searchPlaceholder}
                searchValue={searchValue}
                onSearchChange={onSearchChange}
                onSearchReset={onSearchReset}
                headerExtraContent={headerExtraContent}
                searchInputType={searchInputType}
                searchContainerStyle={searchContainerStyle}
            />
            <FilterMainContent
                mainContent={mainContent}
                maxHeight={mainContentMaxHeight}
            />
        </div>
    );

    const content = (
        <>
            <FilterSelectedTagsSection
                selectedCount={selectedCount}
                totalCount={totalCount}
                selectedTags={selectedTags}
            />
            {searchAndMainContent}
        </>
    );

    if (containerPadding) {
        return (
            <div
                className={`d-flex flex-column flex-grow-1 ${containerPadding} flex-shrink-0`}
            >
                {content}
            </div>
        );
    }

    return content;
};